import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Camera from "@insite/mobius/Icons/Camera";
import Facebook from "@insite/mobius/Icons/Facebook";
import FileText from "@insite/mobius/Icons/FileText";
import Instagram from "@insite/mobius/Icons/Instagram";
import Linkedin from "@insite/mobius/Icons/Linkedin";
import Rss from "@insite/mobius/Icons/Rss";
import Twitter from "@insite/mobius/Icons/Twitter";
import User from "@insite/mobius/Icons/User";
import Video from "@insite/mobius/Icons/Video";
import Youtube from "@insite/mobius/Icons/Youtube";
import { Clickable, LazyImage } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    tittle = "tittle",
    direction = "direction",
    alignment = "alignment",
    iconsContainerAlignment = "iconsContainerAlignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    socialLinks = "socialLinks",
    visibilityState = "visibilityState",
    iconSize = "iconSize",
    iconColor = "iconColor",
    backgroundColor = "backgroundColor",
    linksPerRow = "linksPerRow",
    customCSS = "customCSS",
    logoImage = "logoImage",
    faceBook = "faceBook",
    instragram = "instragram",
    linkedin = "linkedin",
    youtube = "youtube",
    imageAltText = "imageAltText",
}

const enum icons {
    instagram = "Instagram",
    facebook = "Facebook",
    youtube = "Youtube",
    twitter = "Twitter",
    pinterest = "Pinterest",
    rss = "Rss",
    user = "User",
    linkedin = "Linkedin",
    camera = "Camera",
    video = "Video",
    notebook = "Notebook",
}

const enum visibilityState {
    both = "both",
    label = "label",
    icon = "icon",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.title]: string;
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "right";
        [fields.iconsContainerAlignment]: "flex-start" | "flex-end" | "center";
        [fields.visibilityState]: visibilityState;
        [fields.title]: string;
        [fields.iconSize]: number;
        [fields.linksPerRow]: number;
        [fields.iconColor]: string;
        [fields.backgroundColor]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
        [fields.socialLinks]: SocialLinkModel[];
        [fields.customCSS]: string;
        [fields.logoImage]: string;
        [fields.faceBook]: string;
        [fields.instragram]: string;
        [fields.linkedin]: string;
        [fields.youtube]: string;
        [fields.imageAltText]: string;
    };
}

interface SocialLinkModel {
    fields: {
        socialtittle: string;
        altText: string;
        socialIcon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overriddenTitle: string;
    };
}

interface LinkModel {
    fields: {
        title: string;
        icon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overriddenTitle: string;
    };
}

export interface LinkListStyles {
    socialLinkListWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: InjectableCss;
    container?: InjectableCss;
    brandLogocontainer?: InjectableCss;
    header?: LinkPresentationProps;
    title?: InjectableCss;
    socialLogo?: InjectableCss;
    socialLogoConatiner?: InjectableCss;
    brandlogo?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    socialLinkListWrapper: {
        css: css`
            width: 100%;
            padding: 10px 0 14px;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            column-gap: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    linkWrapper: {
        css: css`
            padding: 1px 18px 0 0;
            margin-right: 0;
            flex-shrink: 0;
        `,
    },
    container: {
        css: css`
            margin-top: 33px;
            margin-bottom: 10px;
            display: flex;
            flex-direction: column;
            row-gap: 18px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 0px;
                        margin-bottom: 0px;
                    `,
                    css`
                        margin-top: 0px;
                        margin-bottom: 0px;
                    `,
                    css`
                        margin-top: 0px;
                        margin-bottom: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    header: {
        css: css`
            cursor: default;
            &:hover {
                span[class^="TypographyStyle"] {
                    color: ${getColor("primary.contrast")};
                    cursor: default;
                }
            }
            span[class^="TypographyStyle"] {
                font-style: normal;
                font-weight: 700;
                font-size: 15px;
                line-height: 22px;
                text-transform: uppercase;
                cursor: default;
                color: ${getColor("primary.contrast")};
                margin-bottom: 20px;
                &:hover {
                    color: ${getColor("primary.contrast")};
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-bottom: 9px;
                        `,
                        css`
                            margin-bottom: 9px;
                        `,
                        css`
                            margin-bottom: 9px;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    title: {
        css: css`
            font-style: normal;
            font-weight: 400;
            font-size: 14px;
            line-height: 22px;
            color: ${getColor("primary.contrast")};
            margin-bottom: 27px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 13px;
                    `,
                    css`
                        margin-bottom: 13px;
                    `,
                    css`
                        margin-bottom: 13px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    brandlogo: {
        css: css`
            img {
                max-width: 200px;
                max-height: 100px;
            }
        `,
    },
    socialLogo: {
        css: css`
            img {
                max-width: 24px;
                max-height: 24px;
            }
        `,
    },
    socialLogoConatiner: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            column-gap: 8px;
        `,
    },
    brandLogocontainer: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
};

export const socialLinksStyles = linkListStyles;

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.youtube:
            return Youtube;
        case icons.twitter:
            return Twitter;
        case icons.facebook:
            return Facebook;
        case icons.instagram:
            return Instagram;
        case icons.linkedin:
            return Linkedin;
        case icons.notebook:
            return FileText;
        case icons.rss:
            return Rss;
        case icons.user:
            return User;
        case icons.video:
            return Video;
        case icons.camera:
            return Camera;
        default:
            break;
    }

    return "";
};
export const FooterSocialLinks: FC<OwnProps> = ({ fields }) => {
    // const links = useGetLinks(fields.links, o => o.fields.destination);

    const { links: socialLinks } = useGetLinks(fields.socialLinks, o => o.fields.destination);

    const styles = useMergeStyles("socialLinks", linkListStyles);

    const customCssWrapper = {
        css: css`
            margin-bottom: 20px;
            ${fields.customCSS}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 8px;
                    `,
                    css`
                        margin-bottom: 8px;
                    `,
                    css`
                        margin-bottom: 8px;
                    `,
                    null,
                    null,
                ])};
        `,
    };
    const socialLinkListStyle = css`
        ${fields.direction === "horizontal" ? "flex-direction: row;" : "flex-direction: column;"}
        /* ${fields.backgroundColor && `background-color: ${fields.backgroundColor};`} */
        ${styles.socialLinkListWrapper?.css}
        svg path {
            fill: ${fields.backgroundColor};
        }
        svg {
            stroke: ${fields.iconColor};
        }
        justify-content: ${fields.iconsContainerAlignment};
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    padding: 0px !important;
                `,
                css``,
                css``,
                css``,
                css``,
            ])};
        > .link-wrapper:first-child {
            > a > span:first-child {
                margin-left: 0;
            }
        }
    `;

    const linkWrapperStyle = css`
        padding-right: 0px !important;
        margin-right: 15px;
        ${styles.linkWrapper?.css}
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    padding-right: 0px !important;
                `,
                css``,
                css``,
                css``,
                css``,
            ])};
    `;

    const showIcon = fields.visibilityState === visibilityState.both || fields.visibilityState === visibilityState.icon;
    const showLabel =
        fields.visibilityState === visibilityState.both || fields.visibilityState === visibilityState.label;

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.brandLogocontainer}>
                    <LazyImage src={fields.logoImage} {...styles.brandlogo} altText={fields.imageAltText}></LazyImage>
                </StyledWrapper>

                <StyledWrapper {...styles.socialLogoConatiner} role="list">
                    {socialLinks.map((link, index) => (
                        <StyledWrapper key={index} role="listitem">
                            <Clickable
                                href={link.url}
                                target={fields.socialLinks[index].fields.openInNewWindow ? "_blank" : ""}
                            >
                                <LazyImage
                                    aria-label={`${fields.socialLinks[index].fields.altText}-link`}
                                    altText={fields.socialLinks[index].fields.altText}
                                    src={fields.socialLinks[index].fields.socialIcon}
                                    {...styles.socialLogo}
                                />
                            </Clickable>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.social-links{
}

.link-wrapper{
}

.social-link{
}`;

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "thumbs-up",
    isSystem: true,
    fieldDefinitions: [
        {
            name: fields.logoImage,
            displayName: "CompanyLogo",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.socialLinks,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields) => {
                return item.fields.socialtittle;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "socialtittle",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "altText",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "socialIcon",
                    displayName: "Logo Image",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                },
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Url",
                    },
                    isRequired: true,
                },
                {
                    name: "openInNewWindow",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
            ],
        },
        {
            name: fields.direction,
            displayName: "Display",
            editorTemplate: "RadioButtonsField",
            defaultValue: "horizontal",
            fieldType: "General",
            tab: settingsTab,
            sortOrder: 1,
            options: [
                {
                    displayName: "Column",
                    value: "vertical",
                },
                {
                    displayName: "Row",
                    value: "horizontal",
                },
            ],
        },
        {
            name: fields.alignment,
            displayName: "Text Position",
            editorTemplate: "RadioButtonsField",
            defaultValue: "left",
            fieldType: "General",
            tab: settingsTab,
            sortOrder: 2,
            options: [
                {
                    displayName: "Left of icon",
                    value: "left",
                },
                {
                    displayName: "Right of icon",
                    value: "right",
                },
            ],
        },
        {
            name: fields.iconsContainerAlignment,
            displayName: "Aligment",
            editorTemplate: "RadioButtonsField",
            defaultValue: "flex-start",
            fieldType: "General",
            tab: settingsTab,
            sortOrder: 2,
            options: [
                {
                    displayName: "Algin Left",
                    value: "flex-start",
                },
                {
                    displayName: "Align Center",
                    value: "center",
                },
                {
                    displayName: "Algin Right",
                    value: "flex-end",
                },
            ],
        },
        {
            name: fields.visibilityState,
            displayName: "Visibility",
            editorTemplate: "RadioButtonsField",
            tab: settingsTab,
            options: [
                {
                    displayName: "Show Both",
                    value: visibilityState.both,
                },
                {
                    displayName: "Show Label Only",
                    value: visibilityState.label,
                },
                {
                    displayName: "Show Icon Only",
                    value: visibilityState.icon,
                },
            ],
            defaultValue: visibilityState.both,
            fieldType: "General",
            sortOrder: 4,
        },
        {
            name: fields.iconSize,
            displayName: "Icon Size",
            editorTemplate: "IntegerField",
            defaultValue: 30,
            min: 1,
            fieldType: "General",
            sortOrder: 3,
            tab: settingsTab,
        },
        {
            name: fields.linksPerRow,
            displayName: "Icons Per Row",
            fieldType: "General",
            editorTemplate: "DropDownField",
            options: [
                { value: 1 },
                { value: 2 },
                { value: 3 },
                { value: 4 },
                { value: 5 },
                { value: 6 },
                { value: 7 },
                { value: 8 },
            ],
            sortOrder: 3,
            defaultValue: 8,
            isVisible: (item?: HasFields) => {
                return item?.fields.direction === "horizontal";
            },
            tab: settingsTab,
        },
        {
            name: fields.iconColor,
            displayName: "Icon Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "",
            fieldType: "General",
            sortOrder: 0,
            tab: settingsTab,
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "",
            fieldType: "General",
            sortOrder: 1,
            tab: settingsTab,
        },
        {
            name: fields.customCSS,
            displayName: "Custom CSS",
            editorTemplate: "CodeField",
            fieldType: "General",
            tab: advancedTab,
            defaultValue: defaultCustomCss,
            isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            validate: value => {
                if (value === undefined || value === null) {
                    return "";
                }

                const result = cssLinter.parse(value, { silent: true });
                if (result?.stylesheet?.parsingErrors) {
                    // the error output at this time only has room for one line so we just show the first error
                    return result.stylesheet.parsingErrors.length <= 0
                        ? ""
                        : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                }

                return "Unable to parse Css";
            },
            options: {
                mode: "css",
                lint: true,
                autoRefresh: true,
            },
        },
        {
            name: "imageAltText",
            fieldType: "General",
            editorTemplate: "TextField",
            defaultValue: "",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: FooterSocialLinks,
    definition,
};

export default widgetModule;
